import React, { useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { PRIMARY_BTN, SiteFooter, SiteHeader, demoLink } from '../components/home/SiteChrome';
import { CHECKIN_METHODS, checkInMethod } from '../utils/checkInMethods';

const CheckInMethodPage = () => {
  const { method: slug } = useParams();
  const method = checkInMethod(slug);

  useEffect(() => {
    if (method) {
      document.title = `${method.title} check-in | EventEase`;
      window.scrollTo(0, 0);
    }
  }, [method]);

  if (!method) return <Navigate to="/#check-in" replace />;
  const { icon: Icon } = method;
  const others = CHECKIN_METHODS.filter((m) => m.slug !== method.slug);

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <SiteHeader demoFrom={`check-in/${method.slug}`} />

      <section className="relative overflow-hidden bg-[#160a2b] text-white">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(249,115,22,0.25),transparent_55%),radial-gradient(ellipse_at_bottom_left,rgba(139,92,246,0.35),transparent_55%)]" />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8">
          <a href="/#check-in" className="inline-flex items-center gap-1.5 text-sm text-violet-200 hover:text-white">
            <ArrowLeft className="h-4 w-4" /> All check-in options
          </a>
          <div className="mt-6 flex h-12 w-12 items-center justify-center rounded-xl bg-white/10">
            <Icon className="h-6 w-6 text-orange-300" />
          </div>
          <p className="mt-5 text-sm font-semibold uppercase tracking-wide text-orange-300">{method.title}</p>
          <h1 className="mt-2 text-3xl font-bold leading-tight sm:text-5xl">{method.headline}</h1>
          <p className="mt-5 max-w-2xl text-lg text-slate-300">{method.intro}</p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Button asChild size="lg" className={PRIMARY_BTN}>
              <Link to={demoLink(`check-in/${method.slug}`)}>Book a demo <ArrowRight className="ml-2 h-5 w-5" /></Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold sm:text-3xl">How it works</h2>
          <ol className="mt-8 grid gap-5 sm:grid-cols-2">
            {method.steps.map(([title, text], i) => (
              <li key={title} className="rounded-2xl border border-gray-200 p-6">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-violet-100 text-sm font-bold text-violet-700">{i + 1}</span>
                <h3 className="mt-4 font-semibold">{title}</h3>
                <p className="mt-1.5 text-sm text-gray-600">{text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-gray-50 py-16 sm:py-20">
        <div className="mx-auto grid max-w-5xl gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div>
            <h2 className="text-2xl font-bold">What you get</h2>
            <ul className="mt-5 space-y-3">
              {method.features.map((item) => (
                <li key={item} className="flex gap-2 text-gray-700"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />{item}</li>
              ))}
            </ul>
          </div>
          <div className="space-y-6">
            <div className="rounded-2xl border border-gray-200 bg-white p-6">
              <h2 className="font-semibold">What you need</h2>
              <ul className="mt-3 list-disc space-y-1.5 pl-5 text-sm text-gray-600">
                {method.needs.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </div>
            <div className="rounded-2xl border border-violet-200 bg-violet-50 p-6">
              <h2 className="font-semibold text-violet-900">Best for</h2>
              <p className="mt-2 text-sm text-violet-900/80">{method.bestFor}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold">Mix and match at the same gate</h2>
          <p className="mt-2 max-w-2xl text-gray-600">All four methods run on the same scanner screen and feed the same counts, so each lane can use whatever suits it.</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            {others.map(({ slug, icon: OtherIcon, title, card }) => (
              <Link key={slug} to={`/check-in/${slug}`} className="group rounded-2xl border border-gray-200 p-5 transition hover:border-violet-300 hover:shadow-sm">
                <OtherIcon className="h-6 w-6 text-violet-600" />
                <h3 className="mt-3 font-semibold group-hover:text-violet-700">{title}</h3>
                <p className="mt-1 text-sm text-gray-600">{card}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-r from-violet-700 via-fuchsia-700 to-orange-600 py-14">
        <div className="mx-auto flex max-w-5xl flex-col items-center gap-5 px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white sm:text-3xl">See it working on your own event</h2>
          <Button asChild size="lg" variant="secondary">
            <Link to={demoLink(`check-in/${method.slug}`)}>Book a demo <ArrowRight className="ml-2 h-5 w-5" /></Link>
          </Button>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default CheckInMethodPage;
